import React, { useState, useMemo, useEffect } from 'react';
import { usePlayers } from '../contexts/PlayerContext';
import { Button } from '../components/common/Button';
import { Card } from '../components/common/Card';
import { Modal } from '../components/common/Modal';
import { Input } from '../components/common/Input';
import { Layout } from '../components/layout/Layout';
import { PlayerEditModal } from '../components/players/PlayerEditModal';
import { DragRankingInterface } from '../components/players/DragRankingInterface';
import PositionSlider from '../components/player/PositionSlider';
import { Player, PlayerAttributes, Position } from '../types';
import { apiClient } from '../api/client';

type SortField = 'name' | 'overall_rating' | 'fwd_rating' | 'mid_rating' | 'def_rating' | 'gk_rating' | 'best_position' |
  'passing' | 'first_touch' | 'dribbling' | 'finishing' | 'vision' | 'pace' | 'stamina' | 'agility' | 'acceleration' |
  'strength' | 'balance' | 'decision_making' | 'work_rate' | 'composure' | 'tackling' |
  'marking' | 'anticipation' | 'leadership' | 'defensive_awareness' | 'off_the_ball_movement' | 'teamwork' | 'goalkeeping' |
  'balance_strength' | 'positioning' | 'off_the_ball' | // deprecated
  'manual_fwd_rating' | 'manual_mid_rating' | 'manual_def_rating' | 'manual_gk_rating';
type SortDirection = 'asc' | 'desc';
type TabView = 'overview' | 'edit' | 'rankings' | 'beta-positions';
type RankingMode = 'input' | 'drag';

const ATTRIBUTE_DEFINITIONS: Record<keyof PlayerAttributes, string> = {
  // Technical (5)
  passing: 'Accuracy and quality of passes to teammates. Includes short and long passes.',
  first_touch: 'Ability to control the ball cleanly when receiving it. Good first touch sets up the next action.',
  dribbling: 'Skill at running with the ball under control and ability to beat opponents 1v1.',
  finishing: 'Ability to score goals when presented with chances. Includes accuracy and power of shots.',
  vision: 'Seeing passes and opportunities others miss. Key for playmakers and creative players.', // v2.0
  // Physical (6)
  pace: 'Top running speed. How fast a player can sprint.',
  stamina: 'Fitness and ability to maintain performance throughout the entire match without tiring.',
  agility: 'Ability to change direction quickly, turn sharply, and maneuver in tight spaces.',
  acceleration: 'How quickly a player reaches top speed from a standing start or slow jog.',
  strength: 'Physical power in challenges, shielding the ball, and winning aerial duels.', // v2.0
  balance: 'Stability and body control when moving, turning, or being tackled.', // v2.0
  // Mental (6)
  decision_making: 'Choosing the right option in any situation - when to pass, shoot, dribble, or tackle.',
  work_rate: 'Willingness to put in effort, track back, press opponents, and contribute throughout.',
  composure: 'Staying calm under pressure, making good decisions in high-stakes moments.',
  anticipation: 'Reading the game and predicting what will happen next to get into position early.',
  leadership: 'Ability to organize teammates, communicate effectively, and inspire those around you.',
  teamwork: 'Playing for the team, making the right decisions for the collective rather than individual glory.',
  // Defensive (3)
  tackling: 'Ability to win the ball from opponents through challenges and interceptions.',
  marking: 'Ability to track and stay close to opponents, denying them space and time.',
  defensive_awareness: 'Reading danger, positioning to intercept, and defensive decision-making.', // v2.0
  // Attacking (1)
  off_the_ball_movement: 'Intelligent runs, finding space, and movement to create opportunities.', // v2.0
  // Specialist (1)
  goalkeeping: 'Overall ability in goal including shot-stopping, positioning, reactions, and distribution.',
  // Deprecated (backward compat)
  positioning: '[DEPRECATED] Use Defensive Awareness instead.',
  off_the_ball: '[DEPRECATED] Use Off the Ball Movement instead.',
  balance_strength: '[DEPRECATED] Split into Strength and Balance.',
  // Manual overrides
  manual_fwd_rating: 'Manual override for forward rating (0-10). Blended 50/50 with calculated rating.',
  manual_mid_rating: 'Manual override for midfielder rating (0-10). Blended 50/50 with calculated rating.',
  manual_def_rating: 'Manual override for defender rating (0-10). Blended 50/50 with calculated rating.',
  manual_gk_rating: 'Manual override for goalkeeper rating (0-10). Blended 50/50 with calculated rating.',
};

export function Players() {
  const {
    players,
    loading,
    selectedPlayerIds,
    togglePlayerSelection,
    selectAllPlayers,
    deselectAllPlayers,
    addPlayer,
    deletePlayer,
    updatePlayer,
    fetchPlayers,
  } = usePlayers();

  const [addingPlayer, setAddingPlayer] = useState(false);
  const [newPlayerName, setNewPlayerName] = useState('');
  const [editingPlayer, setEditingPlayer] = useState<Player | null>(null);
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [filterPosition, setFilterPosition] = useState<string>('all');
  const [activeTab, setActiveTab] = useState<TabView>('overview');
  const [editingAttributes, setEditingAttributes] = useState<Record<string, Partial<PlayerAttributes>>>({});
  const [manualRankings, setManualRankings] = useState<Record<string, Partial<Pick<PlayerAttributes, 'manual_fwd_rating' | 'manual_mid_rating' | 'manual_def_rating' | 'manual_gk_rating'>>>>({});
  const [rankingMode, setRankingMode] = useState<RankingMode>('input');

  // BETA features state
  const [betaModeEnabled, setBetaModeEnabled] = useState(false);
  const [loadingBeta, setLoadingBeta] = useState(true);
  const [positionPreferences, setPositionPreferences] = useState<Record<string, number>>({});
  const [savingPositions, setSavingPositions] = useState(false);

  // Load BETA mode status and initialize position preferences
  useEffect(() => {
    const loadBetaMode = async () => {
      try {
        const response = await apiClient.getBetaModeEnabled();
        setBetaModeEnabled(response.beta_mode_enabled);
      } catch (err) {
        console.error('Failed to load BETA mode status:', err);
      } finally {
        setLoadingBeta(false);
      }
    };

    loadBetaMode();
  }, []);

  // Initialize position preferences from players
  useEffect(() => {
    const prefs: Record<string, number> = {};
    players.forEach(player => {
      if (player.position_preference !== null && player.position_preference !== undefined) {
        prefs[player.id] = player.position_preference;
      }
    });
    setPositionPreferences(prefs);
  }, [players]);

  const handleImportCSV = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setImporting(true);
      const formData = new FormData();
      formData.append('file', file);

      const response = await apiClient.importCSV(formData);

      if (response.failed > 0) {
        alert(
          `Import completed!\n\nImported: ${response.imported}\nFailed: ${response.failed}\n\n` +
          response.errors.map((e) => `Row ${e.row}: ${e.name} - ${e.reason}`).join('\n')
        );
      } else {
        alert(`Successfully imported ${response.imported} players!`);
      }

      window.location.reload();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to import CSV');
    } finally {
      setImporting(false);
      e.target.value = '';
    }
  };

  const handleExportCSV = async () => {
    try {
      setExporting(true);
      const blob = await apiClient.exportCSV();

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `teamtactics_players_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to export CSV');
    } finally {
      setExporting(false);
    }
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const handleAttributeChange = (playerId: string, attribute: keyof PlayerAttributes, value: number | null) => {
    setEditingAttributes(prev => ({
      ...prev,
      [playerId]: {
        ...(prev[playerId] || {}),
        [attribute]: value,
      }
    }));
  };

  const handlePositionPreferenceChange = (playerId: string, value: number) => {
    setPositionPreferences(prev => ({
      ...prev,
      [playerId]: value,
    }));
  };

  const handleSavePositionPreference = async (playerId: string) => {
    const value = positionPreferences[playerId];
    if (value === undefined) return;

    try {
      await apiClient.updatePositionPreference(playerId, value);
      // Refresh player data to get updated position preference
      await fetchPlayers();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to save position preference');
    }
  };

  const handleSaveAllPositionPreferences = async () => {
    setSavingPositions(true);
    try {
      // Save all position preferences in parallel
      await Promise.all(
        Object.entries(positionPreferences).map(([playerId, value]) =>
          apiClient.updatePositionPreference(playerId, value)
        )
      );
      // Refresh player data to get updated position preferences
      await fetchPlayers();
      alert('All position preferences saved successfully!');
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to save position preferences');
    } finally {
      setSavingPositions(false);
    }
  };

  const handleSaveAttribute = async (playerId: string, attribute: keyof PlayerAttributes) => {
    const player = players.find(p => p.id === playerId);
    if (!player) return;

    const updatedAttributes = {
      ...player.attributes,
      ...editingAttributes[playerId],
    };

    try {
      await updatePlayer(playerId, { attributes: updatedAttributes });

      // Clear editing state for this player
      setEditingAttributes(prev => {
        const updated = { ...prev };
        delete updated[playerId];
        return updated;
      });
    } catch (err) {
      console.error('Failed to save attribute:', err);
    }
  };

  const getAttributeValue = (player: Player, attribute: keyof PlayerAttributes): number | null => {
    // Check if we have a pending edit
    if (editingAttributes[player.id]?.[attribute] !== undefined) {
      return editingAttributes[player.id][attribute] ?? null;
    }
    // Otherwise return the current value
    return player.attributes?.[attribute] ?? null;
  };

  const sortedAndFilteredPlayers = useMemo(() => {
    let filtered = [...players];

    // Filter by position
    if (filterPosition !== 'all') {
      filtered = filtered.filter(p => p.calculated?.best_position === filterPosition);
    }

    // Sort
    filtered.sort((a, b) => {
      let aVal: any;
      let bVal: any;

      if (sortField === 'name') {
        aVal = a.name.toLowerCase();
        bVal = b.name.toLowerCase();
      } else if (sortField === 'best_position') {
        aVal = a.calculated?.best_position || '';
        bVal = b.calculated?.best_position || '';
      } else if (['overall_rating', 'fwd_rating', 'mid_rating', 'def_rating', 'gk_rating'].includes(sortField)) {
        // Calculated ratings - force convert to number using Number()
        const aRating = a.calculated?.[sortField as keyof typeof a.calculated];
        const bRating = b.calculated?.[sortField as keyof typeof b.calculated];

        aVal = aRating != null ? Number(aRating) : -1;
        bVal = bRating != null ? Number(bRating) : -1;

        if (isNaN(aVal)) aVal = -1;
        if (isNaN(bVal)) bVal = -1;
      } else {
        // Raw attributes - force convert to number using Number()
        const aAttr = a.attributes?.[sortField as keyof PlayerAttributes];
        const bAttr = b.attributes?.[sortField as keyof PlayerAttributes];

        aVal = aAttr != null ? Number(aAttr) : -1;
        bVal = bAttr != null ? Number(bAttr) : -1;

        if (isNaN(aVal)) aVal = -1;
        if (isNaN(bVal)) bVal = -1;
      }

      // For numeric comparisons, explicitly compare as numbers
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
      }

      // For string comparisons
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [players, sortField, sortDirection, filterPosition]);

  const getRatingColor = (rating: number | null) => {
    if (rating === null) return 'text-gray-400';
    if (rating >= 80) return 'text-green-600';
    if (rating >= 60) return 'text-blue-600';
    if (rating >= 40) return 'text-gray-600';
    return 'text-red-600';
  };

  if (loading) {
    return (
      <Layout>
        <div className="space-y-6">
          {/* Header Skeleton */}
          <div className="flex justify-between items-center">
            <div className="h-9 w-32 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
            <div className="h-10 w-32 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
          </div>

          {/* Tabs Skeleton */}
          <div className="border-b border-gray-200 dark:border-gray-700">
            <div className="flex space-x-8">
              <div className="h-10 w-24 bg-gray-200 dark:bg-gray-700 rounded-t animate-pulse"></div>
              <div className="h-10 w-32 bg-gray-200 dark:bg-gray-700 rounded-t animate-pulse"></div>
            </div>
          </div>

          {/* Actions Card Skeleton */}
          <Card>
            <div className="flex items-center justify-between">
              <div className="flex space-x-4">
                <div className="h-8 w-24 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
                <div className="h-8 w-24 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
              </div>
              <div className="flex items-center space-x-4">
                <div className="h-8 w-40 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
                <div className="h-8 w-32 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
                <div className="h-8 w-24 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
              </div>
            </div>
          </Card>

          {/* Table Skeleton */}
          <Card>
            <div className="space-y-4">
              {/* Table Header */}
              <div className="flex space-x-4 pb-3 border-b border-gray-200 dark:border-gray-700">
                <div className="h-4 w-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
                <div className="h-4 w-32 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
                <div className="h-4 w-16 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
                <div className="h-4 w-16 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
                <div className="h-4 w-16 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
                <div className="h-4 w-16 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
                <div className="h-4 w-16 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
              </div>

              {/* Table Rows */}
              {[...Array(5)].map((_, i) => (
                <div key={i} className="flex space-x-4 py-3">
                  <div className="h-4 w-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
                  <div className="h-4 w-32 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
                  <div className="h-4 w-16 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
                  <div className="h-4 w-16 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
                  <div className="h-4 w-16 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
                  <div className="h-4 w-16 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
                  <div className="h-4 w-16 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
                </div>
              ))}
            </div>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Players</h1>
          <Button onClick={() => setAddingPlayer(true)}>+ Add Player</Button>
        </div>

        {/* Tabs - Desktop */}
        <div className="border-b border-gray-200 dark:border-gray-700 hidden md:block">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('overview')}
              className={`${
                activeTab === 'overview'
                  ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
            >
              Overview
            </button>
            <button
              onClick={() => setActiveTab('edit')}
              className={`${
                activeTab === 'edit'
                  ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
            >
              Edit Attributes
            </button>
            <button
              onClick={() => setActiveTab('rankings')}
              className={`${
                activeTab === 'rankings'
                  ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
            >
              Manual Rankings
            </button>
            {!loadingBeta && betaModeEnabled && (
              <button
                onClick={() => setActiveTab('beta-positions')}
                className={`${
                  activeTab === 'beta-positions'
                    ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-2`}
              >
                BETA Positions
                <span className="px-1.5 py-0.5 text-xs font-semibold bg-purple-100 text-purple-700 dark:bg-purple-800 dark:text-purple-200 rounded">
                  BETA
                </span>
              </button>
            )}
          </nav>
        </div>

        {/* Tabs - Mobile Dropdown */}
        <div className="md:hidden">
          <label htmlFor="tab-select" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Select View
          </label>
          <select
            id="tab-select"
            value={activeTab}
            onChange={(e) => setActiveTab(e.target.value as TabView)}
            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-base"
          >
            <option value="overview">Overview</option>
            <option value="edit">Edit Attributes</option>
            <option value="rankings">Manual Rankings</option>
            {!loadingBeta && betaModeEnabled && (
              <option value="beta-positions">BETA Positions</option>
            )}
          </select>
        </div>

        {/* Actions */}
        <Card>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
            <div className="flex flex-wrap gap-2">
              <input
                type="file"
                accept=".csv"
                onChange={handleImportCSV}
                ref={(input) => {
                  if (input) {
                    (window as any).csvFileInput = input;
                  }
                }}
                className="hidden"
              />
              <Button
                variant="secondary"
                size="sm"
                onClick={() => (window as any).csvFileInput?.click()}
                loading={importing}
              >
                {importing ? 'Importing...' : 'Import CSV'}
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={handleExportCSV}
                loading={exporting}
                disabled={players.length === 0}
              >
                {exporting ? 'Exporting...' : 'Export CSV'}
              </Button>
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <div className="flex items-center">
                <label className="text-sm text-gray-600 dark:text-gray-400 mr-2">Filter:</label>
                <select
                  value={filterPosition}
                  onChange={(e) => setFilterPosition(e.target.value)}
                  className="px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="all">All Positions</option>
                  <option value="FWD">FWD</option>
                  <option value="MID">MID</option>
                  <option value="DEF">DEF</option>
                  <option value="GK">GK</option>
                </select>
              </div>
              <span className="text-sm text-gray-600 dark:text-gray-400">
                {sortedAndFilteredPlayers.length} of {players.length} players
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() =>
                  selectedPlayerIds.size === players.length
                    ? deselectAllPlayers()
                    : selectAllPlayers()
                }
              >
                {selectedPlayerIds.size === players.length ? 'Deselect All' : 'Select All'}
              </Button>
            </div>
          </div>
        </Card>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <Card>
            {players.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                No players yet. Click "Add Player" to get started!
              </div>
            ) : (
              <>
                {/* Desktop Table View */}
                <div className="hidden md:block overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                      <tr>
                        <th className="px-4 py-3 text-left">
                          <input
                            type="checkbox"
                            checked={selectedPlayerIds.size === players.length && players.length > 0}
                            onChange={() =>
                              selectedPlayerIds.size === players.length
                                ? deselectAllPlayers()
                                : selectAllPlayers()
                            }
                            className="rounded border-gray-300"
                          />
                        </th>
                        <SortableHeader
                          label="Name"
                          field="name"
                          currentField={sortField}
                          direction={sortDirection}
                          onSort={handleSort}
                        />
                        <SortableHeader
                          label="Overall"
                          field="overall_rating"
                          currentField={sortField}
                          direction={sortDirection}
                          onSort={handleSort}
                        />
                        <SortableHeader
                          label="FWD"
                          field="fwd_rating"
                          currentField={sortField}
                          direction={sortDirection}
                          onSort={handleSort}
                        />
                        <SortableHeader
                          label="MID"
                          field="mid_rating"
                          currentField={sortField}
                          direction={sortDirection}
                          onSort={handleSort}
                        />
                        <SortableHeader
                          label="DEF"
                          field="def_rating"
                          currentField={sortField}
                          direction={sortDirection}
                          onSort={handleSort}
                        />
                        <SortableHeader
                          label="GK"
                          field="gk_rating"
                          currentField={sortField}
                          direction={sortDirection}
                          onSort={handleSort}
                        />
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase">
                          Fav Pos
                        </th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-600 dark:text-gray-400 uppercase">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {sortedAndFilteredPlayers.map((player) => (
                        <tr
                          key={player.id}
                          className="hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                          onClick={(e) => {
                            if (!(e.target as HTMLElement).closest('button, input')) {
                              setEditingPlayer(player);
                            }
                          }}
                        >
                          <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              checked={selectedPlayerIds.has(player.id)}
                              onChange={() => togglePlayerSelection(player.id)}
                              className="rounded border-gray-300"
                            />
                          </td>
                          <td className="px-4 py-3 font-medium text-gray-900 dark:text-gray-100">{player.name}</td>
                          <td className={`px-4 py-3 font-semibold ${getRatingColor(player.calculated?.overall_rating ?? null)}`}>
                            {player.calculated?.overall_rating?.toFixed(1) || '-'}
                          </td>
                          <td className={`px-4 py-3 ${getRatingColor(player.calculated?.fwd_rating ?? null)}`}>
                            {player.calculated?.fwd_rating?.toFixed(1) || '-'}
                            {player.attributes?.manual_fwd_rating && <span className="ml-1 text-xs text-primary-600 dark:text-primary-400">🎯</span>}
                          </td>
                          <td className={`px-4 py-3 ${getRatingColor(player.calculated?.mid_rating ?? null)}`}>
                            {player.calculated?.mid_rating?.toFixed(1) || '-'}
                            {player.attributes?.manual_mid_rating && <span className="ml-1 text-xs text-primary-600 dark:text-primary-400">🎯</span>}
                          </td>
                          <td className={`px-4 py-3 ${getRatingColor(player.calculated?.def_rating ?? null)}`}>
                            {player.calculated?.def_rating?.toFixed(1) || '-'}
                            {player.attributes?.manual_def_rating && <span className="ml-1 text-xs text-primary-600 dark:text-primary-400">🎯</span>}
                          </td>
                          <td className={`px-4 py-3 ${getRatingColor(player.calculated?.gk_rating ?? null)}`}>
                            {player.calculated?.gk_rating?.toFixed(1) || '-'}
                            {player.attributes?.manual_gk_rating && <span className="ml-1 text-xs text-primary-600 dark:text-primary-400">🎯</span>}
                          </td>
                          <td className="px-4 py-3 text-gray-600 dark:text-gray-400">
                            {player.favorite_position || '-'}
                          </td>
                          <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                            <div className="flex items-center justify-end space-x-2">
                              <Button
                                variant="secondary"
                                size="sm"
                                onClick={() => setEditingPlayer(player)}
                              >
                                Edit
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  if (confirm(`Delete ${player.name}?`)) {
                                    deletePlayer(player.id);
                                  }
                                }}
                              >
                                Delete
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Mobile Card View */}
                <div className="md:hidden space-y-3">
                  {sortedAndFilteredPlayers.map((player) => (
                    <div
                      key={player.id}
                      className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 space-y-3"
                    >
                      {/* Header Row */}
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3 flex-1">
                          <input
                            type="checkbox"
                            checked={selectedPlayerIds.has(player.id)}
                            onChange={() => togglePlayerSelection(player.id)}
                            className="mt-1 rounded border-gray-300"
                          />
                          <div className="flex-1">
                            <h3 className="font-semibold text-lg text-gray-900 dark:text-gray-100">{player.name}</h3>
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                              {player.favorite_position || 'No position'}
                            </p>
                          </div>
                        </div>
                        <div className={`text-2xl font-bold ${getRatingColor(player.calculated?.overall_rating ?? null)}`}>
                          {player.calculated?.overall_rating?.toFixed(1) || '-'}
                        </div>
                      </div>

                      {/* Ratings Grid */}
                      <div className="grid grid-cols-3 gap-2">
                        <div className="bg-gray-50 dark:bg-gray-800 rounded p-2 text-center">
                          <div className="text-xs text-gray-600 dark:text-gray-400 mb-1">FWD</div>
                          <div className={`font-semibold ${getRatingColor(player.calculated?.fwd_rating ?? null)}`}>
                            {player.calculated?.fwd_rating?.toFixed(1) || '-'}
                            {player.attributes?.manual_fwd_rating && <span className="ml-1 text-xs">🎯</span>}
                          </div>
                        </div>
                        <div className="bg-gray-50 dark:bg-gray-800 rounded p-2 text-center">
                          <div className="text-xs text-gray-600 dark:text-gray-400 mb-1">MID</div>
                          <div className={`font-semibold ${getRatingColor(player.calculated?.mid_rating ?? null)}`}>
                            {player.calculated?.mid_rating?.toFixed(1) || '-'}
                            {player.attributes?.manual_mid_rating && <span className="ml-1 text-xs">🎯</span>}
                          </div>
                        </div>
                        <div className="bg-gray-50 dark:bg-gray-800 rounded p-2 text-center">
                          <div className="text-xs text-gray-600 dark:text-gray-400 mb-1">DEF</div>
                          <div className={`font-semibold ${getRatingColor(player.calculated?.def_rating ?? null)}`}>
                            {player.calculated?.def_rating?.toFixed(1) || '-'}
                            {player.attributes?.manual_def_rating && <span className="ml-1 text-xs">🎯</span>}
                          </div>
                        </div>
                      </div>

                      {/* GK Rating (if significant) */}
                      {(player.calculated?.gk_rating ?? 0) > 0 && (
                        <div className="bg-blue-50 dark:bg-blue-900/20 rounded p-2">
                          <div className="flex justify-between items-center">
                            <span className="text-xs text-gray-600 dark:text-gray-400">Goalkeeper</span>
                            <span className={`font-semibold ${getRatingColor(player.calculated?.gk_rating ?? null)}`}>
                              {player.calculated?.gk_rating?.toFixed(1) || '-'}
                              {player.attributes?.manual_gk_rating && <span className="ml-1 text-xs">🎯</span>}
                            </span>
                          </div>
                        </div>
                      )}

                      {/* Action Buttons */}
                      <div className="flex gap-2 pt-2 border-t border-gray-200 dark:border-gray-700">
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => setEditingPlayer(player)}
                          className="flex-1"
                        >
                          Edit
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            if (confirm(`Delete ${player.name}?`)) {
                              deletePlayer(player.id);
                            }
                          }}
                          className="flex-1"
                        >
                          Delete
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </Card>
        )}

        {/* Edit Attributes Tab */}
        {activeTab === 'edit' && (
          <Card>
            {players.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                No players yet. Click "Add Player" to get started!
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead className="bg-gray-50 border-b border-gray-200 sticky top-0">
                    <tr>
                      <SortableHeader
                        label="Name"
                        field="name"
                        currentField={sortField}
                        direction={sortDirection}
                        onSort={handleSort}
                      />
                      <SortableHeader
                        label="Passing"
                        field="passing"
                        currentField={sortField}
                        direction={sortDirection}
                        onSort={handleSort}
                        tooltip={ATTRIBUTE_DEFINITIONS.passing}
                      />
                      <SortableHeader
                        label="First Touch"
                        field="first_touch"
                        currentField={sortField}
                        direction={sortDirection}
                        onSort={handleSort}
                        tooltip={ATTRIBUTE_DEFINITIONS.first_touch}
                      />
                      <SortableHeader
                        label="Dribbling"
                        field="dribbling"
                        currentField={sortField}
                        direction={sortDirection}
                        onSort={handleSort}
                        tooltip={ATTRIBUTE_DEFINITIONS.dribbling}
                      />
                      <SortableHeader
                        label="Finishing"
                        field="finishing"
                        currentField={sortField}
                        direction={sortDirection}
                        onSort={handleSort}
                        tooltip={ATTRIBUTE_DEFINITIONS.finishing}
                      />
                      <SortableHeader
                        label="Vision ✨"
                        field="vision"
                        currentField={sortField}
                        direction={sortDirection}
                        onSort={handleSort}
                        tooltip={ATTRIBUTE_DEFINITIONS.vision}
                      />
                      <SortableHeader
                        label="Pace"
                        field="pace"
                        currentField={sortField}
                        direction={sortDirection}
                        onSort={handleSort}
                        tooltip={ATTRIBUTE_DEFINITIONS.pace}
                      />
                      <SortableHeader
                        label="Stamina"
                        field="stamina"
                        currentField={sortField}
                        direction={sortDirection}
                        onSort={handleSort}
                        tooltip={ATTRIBUTE_DEFINITIONS.stamina}
                      />
                      <SortableHeader
                        label="Agility"
                        field="agility"
                        currentField={sortField}
                        direction={sortDirection}
                        onSort={handleSort}
                        tooltip={ATTRIBUTE_DEFINITIONS.agility}
                      />
                      <SortableHeader
                        label="Acceleration"
                        field="acceleration"
                        currentField={sortField}
                        direction={sortDirection}
                        onSort={handleSort}
                        tooltip={ATTRIBUTE_DEFINITIONS.acceleration}
                      />
                      <SortableHeader
                        label="Strength ✨"
                        field="strength"
                        currentField={sortField}
                        direction={sortDirection}
                        onSort={handleSort}
                        tooltip={ATTRIBUTE_DEFINITIONS.strength}
                      />
                      <SortableHeader
                        label="Balance ✨"
                        field="balance"
                        currentField={sortField}
                        direction={sortDirection}
                        onSort={handleSort}
                        tooltip={ATTRIBUTE_DEFINITIONS.balance}
                      />
                      <SortableHeader
                        label="Decision"
                        field="decision_making"
                        currentField={sortField}
                        direction={sortDirection}
                        onSort={handleSort}
                        tooltip={ATTRIBUTE_DEFINITIONS.decision_making}
                      />
                      <SortableHeader
                        label="Work Rate"
                        field="work_rate"
                        currentField={sortField}
                        direction={sortDirection}
                        onSort={handleSort}
                        tooltip={ATTRIBUTE_DEFINITIONS.work_rate}
                      />
                      <SortableHeader
                        label="Composure"
                        field="composure"
                        currentField={sortField}
                        direction={sortDirection}
                        onSort={handleSort}
                        tooltip={ATTRIBUTE_DEFINITIONS.composure}
                      />
                      <SortableHeader
                        label="Tackling"
                        field="tackling"
                        currentField={sortField}
                        direction={sortDirection}
                        onSort={handleSort}
                        tooltip={ATTRIBUTE_DEFINITIONS.tackling}
                      />
                      <SortableHeader
                        label="Marking"
                        field="marking"
                        currentField={sortField}
                        direction={sortDirection}
                        onSort={handleSort}
                        tooltip={ATTRIBUTE_DEFINITIONS.marking}
                      />
                      <SortableHeader
                        label="Def Aware ✨"
                        field="defensive_awareness"
                        currentField={sortField}
                        direction={sortDirection}
                        onSort={handleSort}
                        tooltip={ATTRIBUTE_DEFINITIONS.defensive_awareness}
                      />
                      <SortableHeader
                        label="Anticipation"
                        field="anticipation"
                        currentField={sortField}
                        direction={sortDirection}
                        onSort={handleSort}
                        tooltip={ATTRIBUTE_DEFINITIONS.anticipation}
                      />
                      <SortableHeader
                        label="Leadership"
                        field="leadership"
                        currentField={sortField}
                        direction={sortDirection}
                        onSort={handleSort}
                        tooltip={ATTRIBUTE_DEFINITIONS.leadership}
                      />
                      <SortableHeader
                        label="Off Ball Mvmt ✨"
                        field="off_the_ball_movement"
                        currentField={sortField}
                        direction={sortDirection}
                        onSort={handleSort}
                        tooltip={ATTRIBUTE_DEFINITIONS.off_the_ball_movement}
                      />
                      <SortableHeader
                        label="Teamwork"
                        field="teamwork"
                        currentField={sortField}
                        direction={sortDirection}
                        onSort={handleSort}
                        tooltip={ATTRIBUTE_DEFINITIONS.teamwork}
                      />
                      <SortableHeader
                        label="GK"
                        field="goalkeeping"
                        currentField={sortField}
                        direction={sortDirection}
                        onSort={handleSort}
                        tooltip={ATTRIBUTE_DEFINITIONS.goalkeeping}
                      />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {sortedAndFilteredPlayers.map((player) => (
                      <tr key={player.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                        <td className="px-4 py-2 font-medium text-gray-900 dark:text-gray-100 whitespace-nowrap sticky left-0 bg-white dark:bg-gray-800">
                          {player.name}
                        </td>
                        <TableAttributeCell
                          player={player}
                          attribute="passing"
                          value={getAttributeValue(player, 'passing')}
                          onChange={(val) => handleAttributeChange(player.id, 'passing', val)}
                          onBlur={() => handleSaveAttribute(player.id, 'passing')}
                        />
                        <TableAttributeCell
                          player={player}
                          attribute="first_touch"
                          value={getAttributeValue(player, 'first_touch')}
                          onChange={(val) => handleAttributeChange(player.id, 'first_touch', val)}
                          onBlur={() => handleSaveAttribute(player.id, 'first_touch')}
                        />
                        <TableAttributeCell
                          player={player}
                          attribute="dribbling"
                          value={getAttributeValue(player, 'dribbling')}
                          onChange={(val) => handleAttributeChange(player.id, 'dribbling', val)}
                          onBlur={() => handleSaveAttribute(player.id, 'dribbling')}
                        />
                        <TableAttributeCell
                          player={player}
                          attribute="finishing"
                          value={getAttributeValue(player, 'finishing')}
                          onChange={(val) => handleAttributeChange(player.id, 'finishing', val)}
                          onBlur={() => handleSaveAttribute(player.id, 'finishing')}
                        />
                        <TableAttributeCell
                          player={player}
                          attribute="vision"
                          value={getAttributeValue(player, 'vision')}
                          onChange={(val) => handleAttributeChange(player.id, 'vision', val)}
                          onBlur={() => handleSaveAttribute(player.id, 'vision')}
                        />
                        <TableAttributeCell
                          player={player}
                          attribute="pace"
                          value={getAttributeValue(player, 'pace')}
                          onChange={(val) => handleAttributeChange(player.id, 'pace', val)}
                          onBlur={() => handleSaveAttribute(player.id, 'pace')}
                        />
                        <TableAttributeCell
                          player={player}
                          attribute="stamina"
                          value={getAttributeValue(player, 'stamina')}
                          onChange={(val) => handleAttributeChange(player.id, 'stamina', val)}
                          onBlur={() => handleSaveAttribute(player.id, 'stamina')}
                        />
                        <TableAttributeCell
                          player={player}
                          attribute="agility"
                          value={getAttributeValue(player, 'agility')}
                          onChange={(val) => handleAttributeChange(player.id, 'agility', val)}
                          onBlur={() => handleSaveAttribute(player.id, 'agility')}
                        />
                        <TableAttributeCell
                          player={player}
                          attribute="acceleration"
                          value={getAttributeValue(player, 'acceleration')}
                          onChange={(val) => handleAttributeChange(player.id, 'acceleration', val)}
                          onBlur={() => handleSaveAttribute(player.id, 'acceleration')}
                        />
                        <TableAttributeCell
                          player={player}
                          attribute="strength"
                          value={getAttributeValue(player, 'strength')}
                          onChange={(val) => handleAttributeChange(player.id, 'strength', val)}
                          onBlur={() => handleSaveAttribute(player.id, 'strength')}
                        />
                        <TableAttributeCell
                          player={player}
                          attribute="balance"
                          value={getAttributeValue(player, 'balance')}
                          onChange={(val) => handleAttributeChange(player.id, 'balance', val)}
                          onBlur={() => handleSaveAttribute(player.id, 'balance')}
                        />
                        <TableAttributeCell
                          player={player}
                          attribute="decision_making"
                          value={getAttributeValue(player, 'decision_making')}
                          onChange={(val) => handleAttributeChange(player.id, 'decision_making', val)}
                          onBlur={() => handleSaveAttribute(player.id, 'decision_making')}
                        />
                        <TableAttributeCell
                          player={player}
                          attribute="work_rate"
                          value={getAttributeValue(player, 'work_rate')}
                          onChange={(val) => handleAttributeChange(player.id, 'work_rate', val)}
                          onBlur={() => handleSaveAttribute(player.id, 'work_rate')}
                        />
                        <TableAttributeCell
                          player={player}
                          attribute="composure"
                          value={getAttributeValue(player, 'composure')}
                          onChange={(val) => handleAttributeChange(player.id, 'composure', val)}
                          onBlur={() => handleSaveAttribute(player.id, 'composure')}
                        />
                        <TableAttributeCell
                          player={player}
                          attribute="tackling"
                          value={getAttributeValue(player, 'tackling')}
                          onChange={(val) => handleAttributeChange(player.id, 'tackling', val)}
                          onBlur={() => handleSaveAttribute(player.id, 'tackling')}
                        />
                        <TableAttributeCell
                          player={player}
                          attribute="marking"
                          value={getAttributeValue(player, 'marking')}
                          onChange={(val) => handleAttributeChange(player.id, 'marking', val)}
                          onBlur={() => handleSaveAttribute(player.id, 'marking')}
                        />
                        <TableAttributeCell
                          player={player}
                          attribute="defensive_awareness"
                          value={getAttributeValue(player, 'defensive_awareness')}
                          onChange={(val) => handleAttributeChange(player.id, 'defensive_awareness', val)}
                          onBlur={() => handleSaveAttribute(player.id, 'defensive_awareness')}
                        />
                        <TableAttributeCell
                          player={player}
                          attribute="anticipation"
                          value={getAttributeValue(player, 'anticipation')}
                          onChange={(val) => handleAttributeChange(player.id, 'anticipation', val)}
                          onBlur={() => handleSaveAttribute(player.id, 'anticipation')}
                        />
                        <TableAttributeCell
                          player={player}
                          attribute="leadership"
                          value={getAttributeValue(player, 'leadership')}
                          onChange={(val) => handleAttributeChange(player.id, 'leadership', val)}
                          onBlur={() => handleSaveAttribute(player.id, 'leadership')}
                        />
                        <TableAttributeCell
                          player={player}
                          attribute="off_the_ball_movement"
                          value={getAttributeValue(player, 'off_the_ball_movement')}
                          onChange={(val) => handleAttributeChange(player.id, 'off_the_ball_movement', val)}
                          onBlur={() => handleSaveAttribute(player.id, 'off_the_ball_movement')}
                        />
                        <TableAttributeCell
                          player={player}
                          attribute="teamwork"
                          value={getAttributeValue(player, 'teamwork')}
                          onChange={(val) => handleAttributeChange(player.id, 'teamwork', val)}
                          onBlur={() => handleSaveAttribute(player.id, 'teamwork')}
                        />
                        <TableAttributeCell
                          player={player}
                          attribute="goalkeeping"
                          value={getAttributeValue(player, 'goalkeeping')}
                          onChange={(val) => handleAttributeChange(player.id, 'goalkeeping', val)}
                          onBlur={() => handleSaveAttribute(player.id, 'goalkeeping')}
                        />
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        )}

        {/* Add Player Modal */}
        <Modal
          isOpen={addingPlayer}
          onClose={() => {
            setAddingPlayer(false);
            setNewPlayerName('');
          }}
          title="Add New Player"
        >
          <div className="space-y-4">
            <Input
              label="Player Name"
              value={newPlayerName}
              onChange={(e) => setNewPlayerName(e.target.value)}
              placeholder="Enter player name"
            />
            <div className="flex justify-end space-x-2">
              <Button
                variant="ghost"
                onClick={() => {
                  setAddingPlayer(false);
                  setNewPlayerName('');
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={async () => {
                  if (newPlayerName.trim()) {
                    await addPlayer(newPlayerName.trim());
                    setAddingPlayer(false);
                    setNewPlayerName('');
                  }
                }}
                disabled={!newPlayerName.trim()}
              >
                Add Player
              </Button>
            </div>
          </div>
        </Modal>

        {/* Manual Rankings Tab */}
        {activeTab === 'rankings' && (
          <>
            {/* Mode Toggle */}
            <Card>
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    Manual Rating Mode
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    Choose how you want to set manual position ratings. Ratings are blended 50/50 with calculated ratings.
                  </p>
                </div>
                <div className="flex items-center space-x-2 bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
                  <button
                    onClick={() => setRankingMode('input')}
                    className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                      rankingMode === 'input'
                        ? 'bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 shadow-sm'
                        : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100'
                    }`}
                  >
                    Manual Entry
                  </button>
                  <button
                    onClick={() => setRankingMode('drag')}
                    className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                      rankingMode === 'drag'
                        ? 'bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 shadow-sm'
                        : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100'
                    }`}
                  >
                    Drag Ranking
                  </button>
                </div>
              </div>
            </Card>

            {/* Drag Ranking Interface */}
            {rankingMode === 'drag' && (
              <DragRankingInterface
                players={players}
                onSaveRankings={async (playerId: string, position: Position, rating: number | null) => {
                  const player = players.find((p) => p.id === playerId);
                  if (!player) return;

                  const updates: Partial<PlayerAttributes> = { ...player.attributes };

                  if (position === 'FWD') {
                    updates.manual_fwd_rating = rating;
                  } else if (position === 'MID') {
                    updates.manual_mid_rating = rating;
                  } else if (position === 'DEF') {
                    updates.manual_def_rating = rating;
                  } else if (position === 'GK') {
                    updates.manual_gk_rating = rating;
                  }

                  await updatePlayer(playerId, { attributes: updates });
                }}
              />
            )}

            {/* Manual Input Interface */}
            {rankingMode === 'input' && (
          <Card>
            <div className="space-y-4">
              <div className="text-sm text-gray-600 dark:text-gray-400">
                <p className="mb-2">
                  Manually adjust position ratings to fine-tune team generation. Your manual ratings are blended 50/50 with calculated ratings.
                </p>
                <p className="text-xs">
                  Leave blank to use only calculated ratings. Set a value (1-10) to blend with calculated rating.
                </p>
              </div>

              {players.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  No players yet. Click "Add Player" to get started!
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                      <tr>
                        <SortableHeader
                          label="Player"
                          field="name"
                          currentField={sortField}
                          direction={sortDirection}
                          onSort={handleSort}
                        />
                        <SortableHeader
                          label="FWD Rating"
                          field="manual_fwd_rating"
                          currentField={sortField}
                          direction={sortDirection}
                          onSort={handleSort}
                        />
                        <SortableHeader
                          label="MID Rating"
                          field="manual_mid_rating"
                          currentField={sortField}
                          direction={sortDirection}
                          onSort={handleSort}
                        />
                        <SortableHeader
                          label="DEF Rating"
                          field="manual_def_rating"
                          currentField={sortField}
                          direction={sortDirection}
                          onSort={handleSort}
                        />
                        <SortableHeader
                          label="GK Rating"
                          field="manual_gk_rating"
                          currentField={sortField}
                          direction={sortDirection}
                          onSort={handleSort}
                        />
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-600 dark:text-gray-400 uppercase">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                      {sortedAndFilteredPlayers.map((player) => {
                        const currentRankings = manualRankings[player.id] || {};
                        const hasChanges = Object.keys(currentRankings).length > 0;

                        return (
                          <tr key={player.id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                            <td className="px-4 py-3">
                              <div className="font-medium">{player.name}</div>
                              {player.calculated && (
                                <div className="text-xs text-gray-500 dark:text-gray-400">
                                  Calculated: FWD {player.calculated.fwd_rating} | MID {player.calculated.mid_rating} | DEF {player.calculated.def_rating} | GK {player.calculated.gk_rating}
                                </div>
                              )}
                            </td>
                            <td className="px-4 py-3">
                              <input
                                type="number"
                                min="1"
                                max="10"
                                step="0.1"
                                placeholder="-"
                                maxLength={4}
                                value={currentRankings.manual_fwd_rating ?? player.attributes?.manual_fwd_rating ?? ''}
                                onChange={(e) => {
                                  const val = e.target.value === '' ? null : parseFloat(e.target.value);
                                  if (val === null || (val >= 1 && val <= 10)) {
                                    setManualRankings((prev) => ({
                                      ...prev,
                                      [player.id]: {
                                        ...prev[player.id],
                                        manual_fwd_rating: val,
                                      },
                                    }));
                                  }
                                }}
                                className="w-20 px-2 py-1 text-center border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700"
                              />
                            </td>
                            <td className="px-4 py-3">
                              <input
                                type="number"
                                min="1"
                                max="10"
                                step="0.1"
                                placeholder="-"
                                maxLength={4}
                                value={currentRankings.manual_mid_rating ?? player.attributes?.manual_mid_rating ?? ''}
                                onChange={(e) => {
                                  const val = e.target.value === '' ? null : parseFloat(e.target.value);
                                  if (val === null || (val >= 1 && val <= 10)) {
                                    setManualRankings((prev) => ({
                                      ...prev,
                                      [player.id]: {
                                        ...prev[player.id],
                                        manual_mid_rating: val,
                                      },
                                    }));
                                  }
                                }}
                                className="w-20 px-2 py-1 text-center border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700"
                              />
                            </td>
                            <td className="px-4 py-3">
                              <input
                                type="number"
                                min="1"
                                max="10"
                                step="0.1"
                                placeholder="-"
                                maxLength={4}
                                value={currentRankings.manual_def_rating ?? player.attributes?.manual_def_rating ?? ''}
                                onChange={(e) => {
                                  const val = e.target.value === '' ? null : parseFloat(e.target.value);
                                  if (val === null || (val >= 1 && val <= 10)) {
                                    setManualRankings((prev) => ({
                                      ...prev,
                                      [player.id]: {
                                        ...prev[player.id],
                                        manual_def_rating: val,
                                      },
                                    }));
                                  }
                                }}
                                className="w-20 px-2 py-1 text-center border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700"
                              />
                            </td>
                            <td className="px-4 py-3">
                              <input
                                type="number"
                                min="1"
                                max="10"
                                step="0.1"
                                placeholder="-"
                                maxLength={4}
                                value={currentRankings.manual_gk_rating ?? player.attributes?.manual_gk_rating ?? ''}
                                onChange={(e) => {
                                  const val = e.target.value === '' ? null : parseFloat(e.target.value);
                                  if (val === null || (val >= 1 && val <= 10)) {
                                    setManualRankings((prev) => ({
                                      ...prev,
                                      [player.id]: {
                                        ...prev[player.id],
                                        manual_gk_rating: val,
                                      },
                                    }));
                                  }
                                }}
                                className="w-20 px-2 py-1 text-center border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700"
                              />
                            </td>
                            <td className="px-4 py-3 text-center">
                              <Button
                                size="sm"
                                disabled={!hasChanges}
                                onClick={async () => {
                                  try {
                                    await updatePlayer(player.id, {
                                      attributes: {
                                        ...player.attributes,
                                        ...currentRankings,
                                      },
                                    });
                                    setManualRankings((prev) => {
                                      const { [player.id]: _, ...rest } = prev;
                                      return rest;
                                    });
                                  } catch (err) {
                                    alert(err instanceof Error ? err.message : 'Failed to update rankings');
                                  }
                                }}
                              >
                                Save
                              </Button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </Card>
            )}
          </>
        )}

        {/* BETA Positions Tab */}
        {activeTab === 'beta-positions' && betaModeEnabled && (
          <>
            {/* Info Card */}
            <Card className="border-purple-200 dark:border-purple-800 bg-purple-50 dark:bg-purple-900/10">
              <div className="flex items-center gap-2 mb-2">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  Position Preference Sliders
                </h3>
                <span className="px-2 py-1 text-xs font-semibold bg-purple-100 text-purple-700 dark:bg-purple-800 dark:text-purple-200 rounded">
                  BETA
                </span>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                Set continuous position preferences (0-100 scale) for each player. These replace discrete FWD/MID/DEF positions
                and enable the Model 4 BETA team generation algorithm with weighted ratings.
              </p>
              <div className="flex items-center justify-between">
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  Changes are saved individually per player. Use "Save All" to batch save all changes.
                </div>
                <Button
                  onClick={handleSaveAllPositionPreferences}
                  loading={savingPositions}
                  size="sm"
                >
                  Save All
                </Button>
              </div>
            </Card>

            {/* Position Sliders for Each Player */}
            <div className="space-y-4">
              {sortedAndFilteredPlayers.map(player => {
                const currentValue = positionPreferences[player.id] ?? player.position_preference ?? 50;
                const hasChanges = positionPreferences[player.id] !== undefined &&
                                  positionPreferences[player.id] !== player.position_preference;

                return (
                  <Card key={player.id}>
                    <div className="flex items-start gap-6">
                      {/* Player Info */}
                      <div className="flex-shrink-0 w-48">
                        <div className="font-semibold text-gray-900 dark:text-gray-100 mb-1">
                          {player.name}
                        </div>
                        {player.favorite_position && (
                          <div className="text-sm text-gray-600 dark:text-gray-400">
                            Classic Position: <span className="font-medium">{player.favorite_position}</span>
                          </div>
                        )}
                        {player.calculated?.overall_rating && (
                          <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                            Overall: <span className="font-medium">{player.calculated.overall_rating.toFixed(1)}</span>
                          </div>
                        )}
                      </div>

                      {/* Position Slider */}
                      <div className="flex-1">
                        <PositionSlider
                          value={currentValue}
                          onChange={(value) => handlePositionPreferenceChange(player.id, value)}
                        />
                      </div>

                      {/* Save Button */}
                      <div className="flex-shrink-0 flex flex-col gap-2">
                        <Button
                          onClick={() => handleSavePositionPreference(player.id)}
                          disabled={!hasChanges}
                          size="sm"
                        >
                          Save
                        </Button>
                        {hasChanges && (
                          <div className="text-xs text-orange-600 dark:text-orange-400">
                            Unsaved
                          </div>
                        )}
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>

            {sortedAndFilteredPlayers.length === 0 && (
              <Card>
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  No players found. Add players to set their position preferences.
                </div>
              </Card>
            )}
          </>
        )}

        {/* Edit Player Modal */}
        {editingPlayer && (
          <PlayerEditModal
            isOpen={true}
            onClose={() => setEditingPlayer(null)}
            player={editingPlayer}
          />
        )}
      </div>
    </Layout>
  );
}

// Sortable Header Component
function SortableHeader({
  label,
  field,
  currentField,
  direction,
  onSort,
  tooltip,
}: {
  label: string;
  field: SortField;
  currentField: SortField;
  direction: SortDirection;
  onSort: (field: SortField) => void;
  tooltip?: string;
}) {
  const isActive = currentField === field;

  return (
    <th
      className="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 select-none group relative bg-gray-50 dark:bg-gray-800"
      onClick={() => onSort(field)}
      title={tooltip}
    >
      <div className="flex items-center space-x-1">
        <span>{label}</span>
        {tooltip && <span className="text-gray-400 text-[10px]">ⓘ</span>}
        <span className="text-gray-400">
          {isActive ? (direction === 'asc' ? '↑' : '↓') : '↕'}
        </span>
      </div>
      {/* Tooltip */}
      {tooltip && (
        <div className="invisible group-hover:visible absolute z-10 w-64 p-2 mt-1 text-xs text-white bg-gray-900 rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none normal-case font-normal">
          {tooltip}
          <div className="absolute -top-1 left-4 w-2 h-2 bg-gray-900 transform rotate-45"></div>
        </div>
      )}
    </th>
  );
}

// Table Attribute Cell Component
function TableAttributeCell({
  player,
  attribute,
  value,
  onChange,
  onBlur,
}: {
  player: Player;
  attribute: keyof PlayerAttributes;
  value: number | null;
  onChange: (value: number | null) => void;
  onBlur: () => void;
}) {
  return (
    <td className="px-2 py-2">
      <input
        type="number"
        min="1"
        max="10"
        value={value ?? ''}
        onChange={(e) => {
          const val = e.target.value === '' ? null : parseInt(e.target.value);
          if (val === null || (val >= 1 && val <= 10)) {
            onChange(val);
          }
        }}
        onBlur={onBlur}
        placeholder="-"
        className="w-16 px-2 py-1 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 rounded text-center focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
      />
    </td>
  );
}

// Attribute Input Component
function AttributeInput({
  label,
  value,
  onChange,
  onBlur,
  category,
}: {
  label: string;
  value: number | null;
  onChange: (value: number | null) => void;
  onBlur: () => void;
  category: string;
}) {
  const categoryColors = {
    Technical: 'border-blue-300 focus:ring-blue-500',
    Physical: 'border-green-300 focus:ring-green-500',
    Mental: 'border-purple-300 focus:ring-purple-500',
    GK: 'border-yellow-300 focus:ring-yellow-500',
  };

  return (
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-1">
        {label}
      </label>
      <input
        type="number"
        min="1"
        max="10"
        value={value ?? ''}
        onChange={(e) => {
          const val = e.target.value === '' ? null : parseInt(e.target.value);
          if (val === null || (val >= 1 && val <= 10)) {
            onChange(val);
          }
        }}
        onBlur={onBlur}
        placeholder="-"
        className={`w-full px-3 py-2 border ${categoryColors[category as keyof typeof categoryColors] || 'border-gray-300'} rounded-md focus:outline-none focus:ring-2 text-center font-semibold`}
      />
    </div>
  );
}
